import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import type { OrderFeedback } from '@/bindings/OrderFeedback'
import { Button, type ButtonVariant } from '@/components/button'
import { Field, Textarea } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card } from '@/components/layout'
import { MAX_MESSAGE_CHARS } from '@/features/feedback/api'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { formatDateTime } from '@/lib/time'

/** One textarea and a send button; the message is trimmed and capped at 2000 characters. */
export function FeedbackForm({
  send,
  onSent,
  onCancel,
  compact = false,
}: {
  send: (message: string) => Promise<OrderFeedback>
  onSent: (sent: OrderFeedback) => void
  onCancel?: () => void
  compact?: boolean
}) {
  const t = useT()
  const [message, setMessage] = useState('')
  const mutation = useMutation({ mutationFn: send, onSuccess: onSent })
  const trimmed = message.trim()
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (trimmed) mutation.mutate(trimmed)
      }}
    >
      <Field label={t('feedback.form.label')} hint={t('feedback.form.count', { count: message.length, max: MAX_MESSAGE_CHARS })}>
        <Textarea value={message} maxLength={MAX_MESSAGE_CHARS} rows={compact ? 3 : 5} onChange={(event) => setMessage(event.target.value)} />
      </Field>
      <FormError error={mutation.error} />
      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" size="sm" disabled={mutation.isPending} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" size={compact ? 'sm' : 'md'} disabled={!trimmed} loading={mutation.isPending}>
          {t('feedback.form.send')}
        </Button>
      </div>
    </form>
  )
}

/** What the sender sees once it went: their text, as stored, and when. */
export function SentNote({ feedback }: { feedback: OrderFeedback }) {
  const t = useT()
  const language = useLanguage()
  return (
    <div className="flex flex-col gap-1">
      <p className="text-muted text-sm">{t('feedback.form.sentAt', { time: formatDateTime(feedback.created_at, language) })}</p>
      <p className="whitespace-pre-line">{feedback.message}</p>
    </div>
  )
}

/**
 * An order's feedback slot: the sent text once there is one, the form until
 * then. `sent` comes from the order response so a reload shows the same.
 */
export function OrderFeedbackPanel({
  sent,
  send,
  onSent,
  hint,
}: {
  sent: OrderFeedback | null
  send: (message: string) => Promise<OrderFeedback>
  onSent: (sent: OrderFeedback) => void
  hint?: string
}) {
  const t = useT()
  return (
    <Card aria-label={t('feedback.order.title')} className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('feedback.order.title')}</h2>
      {sent ? (
        <SentNote feedback={sent} />
      ) : (
        <>
          {hint && <p className="text-muted text-sm">{hint}</p>}
          <FeedbackForm send={send} onSent={onSent} compact />
        </>
      )}
    </Card>
  )
}

/** A button that opens the form in place; after sending it shows the text and stays closed. */
export function FeedbackButton({
  label,
  send,
  onSent,
  variant = 'secondary',
  size = 'md',
}: {
  label: string
  send: (message: string) => Promise<OrderFeedback>
  onSent?: (sent: OrderFeedback) => void
  variant?: ButtonVariant
  size?: 'sm' | 'md'
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [sent, setSent] = useState<OrderFeedback | null>(null)
  if (sent) {
    return (
      <Card role="status" aria-label={label} className="flex basis-full flex-col gap-2 p-4">
        <p className="font-medium">{label}</p>
        <p className="text-muted text-sm">{t('feedback.form.thanks')}</p>
        <SentNote feedback={sent} />
      </Card>
    )
  }
  if (!open) {
    return (
      <Button variant={variant} size={size} className="self-start" onClick={() => setOpen(true)}>
        {label}
      </Button>
    )
  }
  return (
    <Card aria-label={label} className="flex basis-full flex-col gap-3 p-4">
      <h2 className="font-medium">{label}</h2>
      <FeedbackForm
        send={send}
        onSent={(sent) => {
          setSent(sent)
          onSent?.(sent)
        }}
        onCancel={() => setOpen(false)}
        compact
      />
    </Card>
  )
}
